import { useMemo } from 'react';
import type {
  DrillSpec,
  DrillEntity,
  DrillCone,
  DrillArrow,
  DrillZone,
  DrillText,
  NormPoint,
} from '@4cm/core/drills/types';
import { pitchPresetById } from '@4cm/core/videoImport/roi';
import './DrillCanvasPreview.css';

interface Props {
  spec: DrillSpec;
  /** Visual width budget in pixels. Height is derived from pitch aspect. */
  width?: number;
}

// -----------------------------------------------------------------------------
// Pitch dimensions + scaling
// -----------------------------------------------------------------------------

const DEFAULT_PITCH = { width: 75, length: 115 }; // yards (full 11v11)
export const DEFAULT_VIEW_WIDTH = 720;

export interface Geometry {
  pitchYdW: number;
  pitchYdL: number;
  pxPerYd: number;
  viewW: number;
  viewH: number;
}

export function geometryFor(spec: DrillSpec, viewWidth: number): Geometry {
  const preset = pitchPresetById(spec.pitchPresetId);
  const pitch = preset
    ? { width: preset.width, length: preset.length }
    : (spec.customPitch ?? DEFAULT_PITCH);
  const pxPerYd = viewWidth / pitch.width;
  return {
    pitchYdW: pitch.width,
    pitchYdL: pitch.length,
    pxPerYd,
    viewW: pitch.width * pxPerYd,
    viewH: pitch.length * pxPerYd,
  };
}

/** normalised (0-1) → pixel position. */
export function nToPx(n: NormPoint, g: Geometry): { x: number; y: number } {
  const [nx, ny] = n;
  return {
    x: nx * g.pitchYdW * g.pxPerYd,
    // SVG y grows downward; pitch y=0 is far end (defending goal at top).
    y: ny * g.pitchYdL * g.pxPerYd,
  };
}

/** pixel → normalised (0-1) position, clamped to [0, 1]. Inverse of nToPx. */
export function pxToN(px: { x: number; y: number }, g: Geometry): NormPoint {
  const totalW = g.pitchYdW * g.pxPerYd;
  const totalH = g.pitchYdL * g.pxPerYd;
  const nx = totalW > 0 ? px.x / totalW : 0;
  const ny = totalH > 0 ? px.y / totalH : 0;
  return [Math.max(0, Math.min(1, nx)), Math.max(0, Math.min(1, ny))];
}

// -----------------------------------------------------------------------------
// Pitch markings — full / half / quarter
// -----------------------------------------------------------------------------

function PitchPaint({ g }: { g: Geometry }) {
  const yd = g.pxPerYd;
  const w = g.viewW;
  const h = g.viewH;
  return (
    <g className="drill-canvas__pitch">
      <rect
        x={0}
        y={0}
        width={w}
        height={h}
        rx={2 * yd}
        fill="rgba(36, 76, 50, 0.78)"
        stroke="rgba(255, 255, 255, 0.4)"
        strokeWidth={0.4 * yd}
      />
      {/* halfway line */}
      <line
        x1={0}
        y1={h / 2}
        x2={w}
        y2={h / 2}
        stroke="rgba(255, 255, 255, 0.4)"
        strokeWidth={0.3 * yd}
      />
      {/* centre circle */}
      <circle
        cx={w / 2}
        cy={h / 2}
        r={10 * yd}
        fill="none"
        stroke="rgba(255, 255, 255, 0.4)"
        strokeWidth={0.3 * yd}
      />
      <circle cx={w / 2} cy={h / 2} r={0.5 * yd} fill="rgba(255, 255, 255, 0.6)" />
      {/* penalty boxes (top + bottom) — 18yd × 44yd centred */}
      {[0, h - 18 * yd].map((boxY, i) => (
        <rect
          key={i}
          x={(g.pitchYdW - 44) / 2 * yd}
          y={boxY}
          width={44 * yd}
          height={18 * yd}
          fill="none"
          stroke="rgba(255, 255, 255, 0.3)"
          strokeWidth={0.3 * yd}
        />
      ))}
    </g>
  );
}

// -----------------------------------------------------------------------------
// Zones
// -----------------------------------------------------------------------------

function ZoneNode({ zone, g }: { zone: DrillZone; g: Geometry }) {
  const fill = zone.fill ?? 'rgba(192, 57, 43, 0.18)';
  const stroke = zone.stroke ?? 'rgba(192, 57, 43, 0.55)';
  const dashArray =
    zone.strokeStyle === 'dashed'
      ? `${0.6 * g.pxPerYd},${0.6 * g.pxPerYd}`
      : zone.strokeStyle === 'dotted'
      ? `${0.2 * g.pxPerYd},${0.4 * g.pxPerYd}`
      : undefined;

  if (zone.shape.kind === 'rect') {
    const tl = nToPx(zone.shape.topLeft, g);
    const br = nToPx(zone.shape.bottomRight, g);
    return (
      <rect
        x={Math.min(tl.x, br.x)}
        y={Math.min(tl.y, br.y)}
        width={Math.abs(br.x - tl.x)}
        height={Math.abs(br.y - tl.y)}
        fill={fill}
        stroke={stroke}
        strokeWidth={0.3 * g.pxPerYd}
        strokeDasharray={dashArray}
      />
    );
  }
  const c = nToPx(zone.shape.center, g);
  return (
    <circle
      cx={c.x}
      cy={c.y}
      r={zone.shape.radiusNorm * g.pitchYdW * g.pxPerYd}
      fill={fill}
      stroke={stroke}
      strokeWidth={0.3 * g.pxPerYd}
      strokeDasharray={dashArray}
    />
  );
}

// -----------------------------------------------------------------------------
// Cones (incl. mannequins, gates, goals)
// -----------------------------------------------------------------------------

function ConeNode({ cone, g }: { cone: DrillCone; g: Geometry }) {
  const { x, y } = nToPx(cone.position, g);
  const yd = g.pxPerYd;
  const variant = cone.variant ?? 'cone';
  const fill = cone.color ?? '#F39C12';
  const transform = cone.rotation ? `rotate(${cone.rotation} ${x} ${y})` : undefined;

  if (variant === 'flat') {
    return (
      <circle cx={x} cy={y} r={0.5 * yd} fill={fill} opacity={0.85} />
    );
  }
  if (variant === 'mannequin') {
    return (
      <g transform={transform}>
        <rect
          x={x - 0.5 * yd}
          y={y - 1.6 * yd}
          width={1 * yd}
          height={3 * yd}
          rx={0.4 * yd}
          fill="rgba(255,255,255,0.75)"
        />
      </g>
    );
  }
  if (variant === 'gate') {
    return (
      <g transform={transform}>
        <line
          x1={x - 1.5 * yd}
          y1={y}
          x2={x + 1.5 * yd}
          y2={y}
          stroke={fill}
          strokeWidth={0.4 * yd}
          strokeLinecap="round"
          strokeDasharray={`${0.6 * yd},${0.4 * yd}`}
        />
      </g>
    );
  }
  if (variant === 'goal_small' || variant === 'goal_full') {
    const gw = (variant === 'goal_full' ? 8 : 5) * yd;
    return (
      <g transform={transform}>
        <rect
          x={x - gw / 2}
          y={y - 0.4 * yd}
          width={gw}
          height={0.8 * yd}
          fill="rgba(255,255,255,0.85)"
          stroke="rgba(0,0,0,0.4)"
          strokeWidth={0.15 * yd}
        />
      </g>
    );
  }
  // default 'cone' — small triangle
  return (
    <polygon
      points={`${x},${y - 0.7 * yd} ${x - 0.6 * yd},${y + 0.5 * yd} ${x + 0.6 * yd},${y + 0.5 * yd}`}
      fill={fill}
    />
  );
}

// -----------------------------------------------------------------------------
// Arrows
// -----------------------------------------------------------------------------

function arrowEndpoints(
  arrow: DrillArrow,
  entityById: Map<string, DrillEntity>,
  g: Geometry,
): { from: { x: number; y: number }; to: { x: number; y: number } } | null {
  const resolve = (anchor: DrillArrow['from']) => {
    if (anchor.kind === 'entity') {
      const e = entityById.get(anchor.entityId);
      return e ? nToPx(e.position, g) : null;
    }
    return nToPx(anchor.position, g);
  };
  const from = resolve(arrow.from);
  const to = resolve(arrow.to);
  if (!from || !to) return null;
  return { from, to };
}

function ArrowNode({
  arrow,
  entityById,
  g,
  markerId,
}: {
  arrow: DrillArrow;
  entityById: Map<string, DrillEntity>;
  g: Geometry;
  markerId: string;
}) {
  const ends = arrowEndpoints(arrow, entityById, g);
  if (!ends) return null;
  const yd = g.pxPerYd;
  const stroke =
    arrow.color ??
    (arrow.kind === 'press' ? '#E74C3C'
    : arrow.kind === 'pass' ? '#3498DB'
    : arrow.kind === 'dribble' ? '#F1C40F'
    : arrow.kind === 'cover' ? '#9B59B6'
    : '#ECF0F1');
  const dashArray =
    arrow.style === 'dashed' ? `${0.8 * yd},${0.6 * yd}`
    : arrow.style === 'dotted' ? `${0.2 * yd},${0.4 * yd}`
    : undefined;

  // Catmull-Rom curvature: bend perpendicular to the chord at midpoint.
  let pathD: string;
  if (arrow.curvature && arrow.curvature > 0.01) {
    const mx = (ends.from.x + ends.to.x) / 2;
    const my = (ends.from.y + ends.to.y) / 2;
    const dx = ends.to.x - ends.from.x;
    const dy = ends.to.y - ends.from.y;
    const len = Math.hypot(dx, dy) || 1;
    const offset = arrow.curvature * len * 0.4;
    const cx = mx + (-dy / len) * offset;
    const cy = my + (dx / len) * offset;
    pathD = `M ${ends.from.x} ${ends.from.y} Q ${cx} ${cy} ${ends.to.x} ${ends.to.y}`;
  } else {
    pathD = `M ${ends.from.x} ${ends.from.y} L ${ends.to.x} ${ends.to.y}`;
  }

  return (
    <g>
      <path
        d={pathD}
        fill="none"
        stroke={stroke}
        strokeWidth={0.4 * yd}
        strokeLinecap="round"
        strokeDasharray={dashArray}
        markerEnd={`url(#${markerId})`}
      />
      {arrow.label && (
        <text
          x={(ends.from.x + ends.to.x) / 2}
          y={(ends.from.y + ends.to.y) / 2 - 0.6 * yd}
          fill="rgba(255,255,255,0.85)"
          fontSize={1.2 * yd}
          textAnchor="middle"
        >
          {arrow.label}
        </text>
      )}
    </g>
  );
}

// -----------------------------------------------------------------------------
// Entities (player tokens / ball)
// -----------------------------------------------------------------------------

function EntityNode({ entity, g }: { entity: DrillEntity; g: Geometry }) {
  const { x, y } = nToPx(entity.position, g);
  const yd = g.pxPerYd;
  const isBall = entity.type === 'ball';
  const r = isBall ? 0.6 * yd : 1.2 * yd;
  const fill =
    entity.color ??
    (isBall ? '#FFFFFF'
    : entity.type === 'home' ? '#2980B9'
    : entity.type === 'away' ? '#C0392B'
    : entity.type === 'official' ? '#7F8C8D'
    : '#95A5A6');
  return (
    <g>
      <circle
        cx={x}
        cy={y}
        r={r}
        fill={fill}
        stroke="rgba(0,0,0,0.45)"
        strokeWidth={0.15 * yd}
      />
      {!isBall && entity.label && (
        <text
          x={x}
          y={y + 0.4 * yd}
          textAnchor="middle"
          fill="white"
          fontSize={1.1 * yd}
          fontWeight={600}
          pointerEvents="none"
        >
          {entity.label}
        </text>
      )}
    </g>
  );
}

// -----------------------------------------------------------------------------
// Free-form text labels
// -----------------------------------------------------------------------------

function TextNode({ text, g }: { text: DrillText; g: Geometry }) {
  const { x, y } = nToPx(text.position, g);
  const fontSize = (text.fontSize ?? 1.4) * g.pxPerYd;
  const transform = text.rotation ? `rotate(${text.rotation} ${x} ${y})` : undefined;
  const lines = text.content.split('\n');
  return (
    <g transform={transform}>
      {lines.map((line, i) => (
        <text
          key={i}
          x={x}
          y={y + i * fontSize * 1.2}
          fill={text.color ?? 'rgba(255,255,255,0.9)'}
          fontSize={fontSize}
          textAnchor="middle"
        >
          {line}
        </text>
      ))}
    </g>
  );
}

// -----------------------------------------------------------------------------
// Top-level preview
// -----------------------------------------------------------------------------

/**
 * Read-only SVG preview of a DrillSpec. Renders pitch + zones + cones +
 * arrows + tokens + free-form labels at their normalised positions. No
 * editor controls, no zoom-pan, no animation playback — those are
 * subsequent sub-passes of the Drill Brain port (DrillCanvas in
 * apps/web is the editable + animated version, ~733 LOC; this preview
 * is intentionally minimal so a coach can SEE the drill Brain drafted
 * before saving it).
 */
export function DrillCanvasPreview({ spec, width = DEFAULT_VIEW_WIDTH }: Props) {
  const g = useMemo(() => geometryFor(spec, width), [spec, width]);
  const entityById = useMemo(
    () => new Map((spec.entities ?? []).map((e) => [e.id, e])),
    [spec.entities],
  );
  const markerId = useMemo(() => `dcp-arrow-${Math.random().toString(36).slice(2, 8)}`, []);

  return (
    <div className="drill-canvas-preview" style={{ width: g.viewW }}>
      <svg
        viewBox={`0 0 ${g.viewW} ${g.viewH}`}
        width={g.viewW}
        height={g.viewH}
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', height: 'auto', display: 'block' }}
        role="img"
        aria-label="Drill diagram preview"
      >
        <defs>
          <marker
            id={markerId}
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth={4}
            markerHeight={4}
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill="currentColor" />
          </marker>
        </defs>
        <PitchPaint g={g} />
        {(spec.zones ?? []).map((z) => (
          <ZoneNode key={z.id} zone={z} g={g} />
        ))}
        {(spec.cones ?? []).map((c) => (
          <ConeNode key={c.id} cone={c} g={g} />
        ))}
        {(spec.arrows ?? []).map((a) => (
          <ArrowNode key={a.id} arrow={a} entityById={entityById} g={g} markerId={markerId} />
        ))}
        {(spec.entities ?? []).map((e) => (
          <EntityNode key={e.id} entity={e} g={g} />
        ))}
        {(spec.texts ?? []).map((t) => (
          <TextNode key={t.id} text={t} g={g} />
        ))}
      </svg>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Convenience: extract a DrillSpec from an arbitrary draft payload.
// Brain's drill_compose tool may place the spec at payload.drill_spec, or
// the payload itself may BE a DrillSpec (depending on the tool variant).
// Returns null if no recognisable spec is present.
// -----------------------------------------------------------------------------

export function extractDrillSpec(payload: unknown): DrillSpec | null {
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as Record<string, unknown>;
  // Direct: payload IS a DrillSpec (has entities + cones + arrows arrays).
  if (Array.isArray(p.entities) && Array.isArray(p.cones) && Array.isArray(p.arrows)) {
    return p as unknown as DrillSpec;
  }
  // Nested: payload.drill_spec is a DrillSpec.
  const nested = p.drill_spec;
  if (nested && typeof nested === 'object') {
    const n = nested as Record<string, unknown>;
    if (Array.isArray(n.entities) && Array.isArray(n.cones) && Array.isArray(n.arrows)) {
      return nested as unknown as DrillSpec;
    }
  }
  return null;
}
